import type { ReviewStats } from '@/api/client'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

const CATS = ['accuracy', 'value', 'cleanliness', 'location', 'check_in', 'communication'] as const
type Cat = (typeof CATS)[number]

const LABEL: Record<Cat, string> = {
  accuracy: 'Accuracy',
  value: 'Value',
  cleanliness: 'Cleanliness',
  location: 'Location',
  check_in: 'Check-in',
  communication: 'Communication',
}

/**
 * Which sub-rating is actually costing stars, portfolio-wide.
 *
 * The heatmap beside this holds the same six numbers, but rendered as six
 * equal cells a 0.16 gap between 4.83 and 4.99 is invisible — every one of
 * them rounds to "about five". Ranking by shortfall from 5.00 and scaling
 * the bar to the worst one turns the same data into a finding.
 *
 * The star counts matter as much as the mean: Airbnb reports each sub-rating
 * as whole stars, so an average is really a mix, and "11 guests marked value
 * down" is a number you can act on where "4.83" is not.
 */
export function SubratingShortfall({ stats }: { stats: ReviewStats }) {
  const sub = stats.portfolio.subratings
  const starsBy = stats.portfolio.subrating_stars
  const ranked = CATS.filter((c): c is Cat => typeof sub[c] === 'number').sort(
    (a, b) => (sub[a] as number) - (sub[b] as number)
  )
  if (ranked.length === 0) return null

  const worstGap = 5 - (sub[ranked[0]] as number)
  // Every sub-rating sits between 4.8 and 5.0, so a bar drawn from zero is
  // six identical full bars. Scaling to the worst gap is what makes the
  // ranking legible; the numbers stay on the row so the scale can't mislead.
  const barPct = (v: number) => (worstGap <= 0 ? 0 : Math.max(2, ((5 - v) / worstGap) * 100))

  // Which category drags each property down, counted across the portfolio.
  // The properties table shows weakest_subrating row by row; nothing has
  // ever aggregated it, and the aggregate is the actually useful form.
  const weakestCounts = new Map<string, number>()
  for (const p of stats.properties) {
    if (!p.weakest_subrating) continue
    weakestCounts.set(p.weakest_subrating, (weakestCounts.get(p.weakest_subrating) ?? 0) + 1)
  }
  const worstCat = ranked[0]
  const worstCatProperties = weakestCounts.get(worstCat) ?? 0
  const propertiesWithAWeakest = stats.properties.filter(p => p.weakest_subrating).length

  return (
    <Card>
      <CardHeader>
        <CardTitle>Which category costs you stars</CardTitle>
        <CardDescription>
          How far each sub-rating falls short of 5.00, worst first, with how the reviews that
          reported it actually scored it.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-2.5">
          {ranked.map((c, i) => {
            const value = sub[c] as number
            const hist = starsBy?.[c]
            const marked = hist ? hist[4] + hist[3] + hist[2] + hist[1] : null
            const total = hist ? hist[5] + (marked ?? 0) : null
            const worst = i === 0
            return (
              <div key={c} className="grid grid-cols-[104px_1fr_44px] items-center gap-3">
                <span className={`text-xs ${worst ? 'text-amber' : 'text-textMuted'}`}>
                  {LABEL[c]}
                </span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div tabIndex={0} className="focus-visible:outline-hidden">
                      <div className="h-2 overflow-hidden rounded-sm bg-surface2">
                        <div
                          className={`h-2 rounded-sm ${worst ? 'bg-amber' : 'bg-borderStrong'}`}
                          style={{ width: `${barPct(value)}%` }}
                        />
                      </div>
                      {hist && (
                        <div className="mt-1 text-[10px] text-textMuted">
                          {hist[5]} gave five
                          {hist[4] > 0 && ` · ${hist[4]} gave four`}
                          {hist[3] > 0 && ` · ${hist[3]} gave three`}
                          {hist[2] + hist[1] > 0 && ` · ${hist[2] + hist[1]} gave two or less`}
                        </div>
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    {total != null
                      ? `${LABEL[c]}: ${marked} of ${total} reviews scored it below five`
                      : `${LABEL[c]}: ${value.toFixed(2)}`}
                  </TooltipContent>
                </Tooltip>
                <span
                  className={`text-right text-xs tabular-nums ${worst ? 'text-amber' : 'text-textMuted'}`}
                >
                  {value.toFixed(2)}
                </span>
              </div>
            )
          })}
        </div>

        {worstCatProperties > 0 && (
          <p className="mt-4 border-t border-border pt-3 text-xs text-textMuted">
            {LABEL[worstCat]} is the weakest category at{' '}
            <span className="text-text">
              {worstCatProperties} of {propertiesWithAWeakest}
            </span>{' '}
            properties.
          </p>
        )}
      </CardContent>
    </Card>
  )
}
